import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TablePagination,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  TableSortLabel,
  Collapse,
  Divider,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Stack,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

/* -------- Icons -------- */
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

/* -------- Framer Motion -------- */
import { motion, AnimatePresence } from "framer-motion";
import { styled } from "@mui/system";

/* -------- Toastify -------- */
import { toast } from "react-toastify";
import {
  getPlatformTenant,
  getSelectedPlatformTenant,
  mapLaravelError,
} from "../../../services/platformTenantsApi";
import {
  evaluateLicenseProvisioning,
  formatLicenseSummary,
  formatLicenseWindow,
  licenseStatusColor,
  licenseStatusLabel,
} from "../../../utils/tenantLicense";

const MotionTableRow = styled(motion.tr)({});
const rowVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, x: 40 },
};

const safeList = (res) => res?.data?.data ?? res?.data ?? [];

const str = (v) => (v == null ? "" : String(v));
const lower = (v) => str(v).toLowerCase().trim();

function HrAdminUsers() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isPlatformAdmin = localStorage.getItem("is_platform_admin") === "1";
  const currentEmployeeCode = localStorage.getItem("employee_code") || "";
  const selectedPlatformTenant = getSelectedPlatformTenant();
  const hasPlatformTenantSelection = Boolean(selectedPlatformTenant?.key);

  const [users, setUsers] = useState([]);
  // Start in the loading state so the page doesn't briefly render
  // "Showing 0 of 0 users" before the first fetch resolves.
  const [loading, setLoading] = useState(true);
  const [tenantLicensePayload, setTenantLicensePayload] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(false);

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);

  // confirm toggle dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // sorting
  const [orderBy, setOrderBy] = useState("employee_code");
  const [order, setOrder] = useState("asc"); // asc | desc

  // filters
  const [filters, setFilters] = useState({
    status: "all",
    role: "all",
    actual_role: "all",
    department: "all",
    site: "all",
    assets: "all", // all | with_assets | no_assets
    missingEmail: false,
  });

  /* ---------- navigation helpers ---------- */
  const goToAdd = () => navigate("/admin/users/new");
  const goToImport = () => navigate("/admin/users/import");
  const goToEdit = (id) => navigate(`/admin/users/edit/${id}`);
  const goToShow = (id) => navigate(`/admin/users/${id}`);

  /* ---------- fetch data ---------- */
  const fetchUsers = useCallback(() => {
    if (isPlatformAdmin && !hasPlatformTenantSelection) {
      setUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    api
      .get("/admin/users")
      .then((res) => {
        const list = safeList(res);
        setUsers(Array.isArray(list) ? list : []);
        setPage(0);
      })
      .catch((err) => {
        console.error("Failed to fetch users:", err);
        toast.error(mapLaravelError(err).message || "Failed to load users.");
      })
      .finally(() => setLoading(false));
  }, [hasPlatformTenantSelection, isPlatformAdmin]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!isPlatformAdmin || !hasPlatformTenantSelection || !selectedPlatformTenant?.id) {
      setTenantLicensePayload(null);
      setLicenseLoading(false);
      return;
    }

    let active = true;
    setLicenseLoading(true);

    getPlatformTenant(selectedPlatformTenant.id)
      .then((payload) => {
        if (!active) return;
        setTenantLicensePayload(payload || null);
      })
      .catch(() => {
        if (!active) return;
        setTenantLicensePayload(null);
      })
      .finally(() => {
        if (active) {
          setLicenseLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [hasPlatformTenantSelection, isPlatformAdmin, selectedPlatformTenant?.id]);

  /* ---------- helpers for display values ---------- */
  const getDeptLabel = (u) =>
    u?.departmentObj?.name || u?.departmentObj?.code || u?.department || "—";

  const getManagerLabel = (u) =>
    u?.manager?.name ||
    (u?.manager_id ? `Manager Code: ${u.manager_id}` : "") ||
    "—";

  const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  };

  const fmtDateTime = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /* ---------- option lists (derived) ---------- */
  const roleOptions = useMemo(() => {
    const s = new Set(users.map((u) => str(u.role).trim()).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const actualRoleOptions = useMemo(() => {
    const s = new Set(users.map((u) => str(u.actual_role).trim()).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const departmentOptions = useMemo(() => {
    const s = new Set(users.map((u) => str(getDeptLabel(u)).trim()).filter((v) => v && v !== "—"));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const siteOptions = useMemo(() => {
    const s = new Set(users.map((u) => str(u.site).trim()).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [users]);

  /* ---------- stats ---------- */
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => lower(u.status) === "active").length;
    const inactive = users.filter((u) => lower(u.status) === "inactive").length;
    const suspended = users.filter((u) => lower(u.status) === "suspended").length;
    return { total, active, inactive, suspended };
  }, [users]);

  const currentLicense = tenantLicensePayload?.current_license || null;
  const licenseUsage = tenantLicensePayload?.license_usage || null;
  const licenseStatus = tenantLicensePayload?.license_status || "";
  const nextLicense = tenantLicensePayload?.next_license || null;
  const toggleAssessment = useMemo(() => {
    if (!selectedUser || lower(selectedUser.status) === "active") {
      return { blocked: false, messages: [] };
    }

    return evaluateLicenseProvisioning(tenantLicensePayload, {
      existingUser: selectedUser,
      targetRole: selectedUser.role,
      targetStatus: "active",
    });
  }, [selectedUser, tenantLicensePayload]);

  /* ---------- filtering + sorting ---------- */
  const filteredUsers = useMemo(() => {
    let list = [...users];

    const q = lower(searchTerm);
    if (q) {
      list = list.filter((u) => {
        const hay = [
          u.employee_code,
          u.name,
          u.email,
          getDeptLabel(u),
          u.position,
          u.role,
          u.actual_role,
          u.site,
          u.cost_center,
          u.nationality,
          u.contract_type,
          u.manager_id,
        ]
          .map((x) => lower(x))
          .join(" ");
        return hay.includes(q);
      });
    }

    if (filters.status !== "all") {
      list = list.filter((u) => lower(u.status) === filters.status);
    }

    if (filters.role !== "all") {
      list = list.filter((u) => lower(u.role) === lower(filters.role));
    }

    if (filters.actual_role !== "all") {
      list = list.filter((u) => lower(u.actual_role) === lower(filters.actual_role));
    }

    if (filters.department !== "all") {
      list = list.filter((u) => lower(getDeptLabel(u)) === lower(filters.department));
    }

    if (filters.site !== "all") {
      list = list.filter((u) => lower(u.site) === lower(filters.site));
    }

    // optional assets_count if backend provides it
    if (filters.assets !== "all") {
      list = list.filter((u) => {
        const c = Number(u.assets_count ?? u.assetsCount ?? 0) || 0;
        return filters.assets === "with_assets" ? c > 0 : c === 0;
      });
    }

    if (filters.missingEmail) {
      list = list.filter((u) => !str(u.email).trim());
    }

    // sorting
    const dir = order === "asc" ? 1 : -1;
    const getSortValue = (u) => {
      switch (orderBy) {
        case "employee_code":
          return lower(u.employee_code);
        case "name":
          return lower(u.name);
        case "department":
          return lower(getDeptLabel(u));
        case "updated_at":
          return new Date(u.updated_at || 0).getTime() || 0;
        default:
          return lower(u.employee_code);
      }
    };

    list.sort((a, b) => {
      const av = getSortValue(a);
      const bv = getSortValue(b);

      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });

    return list;
  }, [users, searchTerm, filters, orderBy, order]);

  /* ---------- pagination ---------- */
  const start = page * rowsPerPage;
  const currentUsers = filteredUsers.slice(start, start + rowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, filters, orderBy, order]);

  const handleChangePage = (_, p) => setPage(p);
  const handleRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  /* ---------- expand row ---------- */
  const isExpanded = (id) => expandedIds.has(id);
  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* ---------- sorting handlers ---------- */
  const handleRequestSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setOrderBy(key);
    setOrder("asc");
  };

  /* ---------- helper for chip colors ---------- */
  const getStatusColor = (status) => {
    switch (lower(status)) {
      case "active":
        return "success";
      case "inactive":
        return "default";
      case "suspended":
        return "warning";
      default:
        return "default";
    }
  };

  const getRoleColor = (role) => {
    switch (lower(role)) {
      case "admin":
        return "warning";
      case "hr":
      case "hr_admin":
        return "info";
      case "ceo":
        return "secondary";
      default:
        return "default";
    }
  };

  const getActualRoleColor = (role) => {
    switch (lower(role)) {
      case "project_manager":
      case "pmc_project_manager":
        return "info";
      case "operation_manager":
        return "warning";
      case "head_of_department":
        return "secondary";
      case "line_employee":
        return "default";
      default:
        return "default";
    }
  };

  /* ---------- quick actions ---------- */
  const askToggle = (u) => {
    setSelectedUser(u);
    setConfirmOpen(true);
  };

  const isCurrentUser = (u) => currentEmployeeCode && str(u?.employee_code) === currentEmployeeCode;

  const askDelete = (u) => {
    if (isCurrentUser(u)) {
      toast.error("You cannot archive your own account.");
      return;
    }

    setDeleteTarget(u);
    setDeleteOpen(true);
  };

  const confirmToggle = async () => {
    if (!selectedUser) return;

    if (toggleAssessment.blocked) {
      toast.error(toggleAssessment.messages[0] || "This activation is blocked by the current tenant license.");
      return;
    }

    setToggleBusy(true);

    // If user is active and we are about to deactivate: check assets
    if (lower(selectedUser.status) === "active") {
      try {
        const { data } = await api.get(`/assets/employee/${selectedUser.employee_code}`);
        if (Array.isArray(data) && data.length > 0) {
          toast.error(
            `Cannot deactivate "${selectedUser.name}" while they have assigned assets. Please unassign assets first.`
          );
          setConfirmOpen(false);
          setSelectedUser(null);
          setToggleBusy(false);
          return;
        }
      } catch (err) {
        console.error("Error checking assigned assets:", err);
        toast.error("Failed to check assigned assets. Please try again later.");
        setConfirmOpen(false);
        setSelectedUser(null);
        setToggleBusy(false);
        return;
      }
    }

    try {
      await api.patch(`/admin/users/${selectedUser.id}/toggle-status`);
      toast.success("User status updated.");
      fetchUsers();
      if (isPlatformAdmin && hasPlatformTenantSelection && selectedPlatformTenant?.id) {
        getPlatformTenant(selectedPlatformTenant.id)
          .then((payload) => setTenantLicensePayload(payload || null))
          .catch(() => {});
      }
    } catch (err) {
      console.error("Failed to toggle user status:", err);
      toast.error(mapLaravelError(err).message || "Failed to update user status.");
    } finally {
      setConfirmOpen(false);
      setSelectedUser(null);
      setToggleBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleteBusy(true);

    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`Archived ${deleteTarget.name || "user"}.`);
      fetchUsers();
      if (isPlatformAdmin && hasPlatformTenantSelection && selectedPlatformTenant?.id) {
        getPlatformTenant(selectedPlatformTenant.id)
          .then((payload) => setTenantLicensePayload(payload || null))
          .catch(() => {});
      }
    } catch (err) {
      console.error("Failed to archive user:", err);
      toast.error(mapLaravelError(err).message || "Failed to archive user.");
    } finally {
      setDeleteBusy(false);
      setDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  /* ---------- reset filters ---------- */
  const resetFilters = () => {
    setSearchTerm("");
    setFilters({
      status: "all",
      role: "all",
      actual_role: "all",
      department: "all",
      site: "all",
      assets: "all",
      missingEmail: false,
    });
    setOrderBy("employee_code");
    setOrder("asc");
    setExpandedIds(new Set());
    setPage(0);
  };

  /* ---------- skeleton placeholders ---------- */
  const renderSkeletonRows = (count = 8) =>
    Array.from(new Array(count)).map((_, index) => (
      <TableRow key={index}>
        <TableCell width={48}>
          <Skeleton width={24} />
        </TableCell>
        <TableCell>
          <Skeleton width={110} />
        </TableCell>
        <TableCell>
          <Skeleton width={160} />
        </TableCell>
        <TableCell>
          <Skeleton width={180} />
        </TableCell>
        <TableCell>
          <Skeleton width={140} />
        </TableCell>
        <TableCell>
          <Skeleton width={90} />
        </TableCell>
        <TableCell>
          <Skeleton width={110} />
        </TableCell>
        <TableCell>
          <Skeleton width={80} />
        </TableCell>
        <TableCell align="right">
          <Skeleton width={90} />
        </TableCell>
      </TableRow>
    ));

  /* ---------- small stat card ---------- */
  const StatCard = ({ label, value, color, onClick }) => (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 1.25,
        borderRadius: "8px",
        border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.9),
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 140ms ease, background-color 140ms ease",
        "&:hover": onClick
          ? {
              borderColor: alpha(theme.palette.primary.main, 0.32),
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.04 : 0.1),
            }
          : {},
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 950, mt: 0.25, color: color || "text.primary" }}>
        {value}
      </Typography>
    </Paper>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 1.25, md: 2 },
        background:
          theme.hr?.gradient?.page ||
          `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${theme.palette.background.default} 42%)`,
        "& .MuiChip-root": {
          borderRadius: "8px",
          fontWeight: 800,
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
        },
        "& .MuiButton-root": {
          borderRadius: "8px",
        },
      }}
    >
      {isPlatformAdmin ? (
        <Alert severity={hasPlatformTenantSelection ? "info" : "warning"} sx={{ mb: 1.5, borderRadius: "8px" }}>
          {hasPlatformTenantSelection ? (
            <>
              Managing tenant <b>{selectedPlatformTenant?.name || selectedPlatformTenant?.key}</b>. Active users are treated as consumed license seats for this tenant.
            </>
          ) : (
            <>
              Select a tenant from the super admin dashboard before opening user management.
              {" "}
              <Button size="small" onClick={() => navigate("/super-admin/dashboard")} sx={{ ml: 1 }}>
                Open dashboard
              </Button>
            </>
          )}
        </Alert>
      ) : null}

      {isPlatformAdmin && hasPlatformTenantSelection ? (
        <Paper
          variant="outlined"
          sx={{
            mb: 1.5,
            p: 1.5,
            borderRadius: "8px",
            borderColor: theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16),
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.9),
          }}
        >
          <Stack spacing={1.25}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Tenant License
              </Typography>
              <Chip size="small" color={licenseStatusColor(licenseStatus)} label={licenseStatusLabel(licenseStatus)} />
            </Stack>

            {licenseLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading current license...
              </Typography>
            ) : currentLicense ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {formatLicenseWindow(currentLicense)} • {formatLicenseSummary(licenseUsage)}
                </Typography>
                {nextLicense ? (
                  <Typography variant="caption" color="text.secondary">
                    Next license starts {nextLicense.valid_from}.
                  </Typography>
                ) : null}
                {Array.isArray(licenseUsage?.per_role) && licenseUsage.per_role.length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {licenseUsage.per_role.map((roleLimit) => (
                      <Chip
                        key={roleLimit.role}
                        size="small"
                        color={roleLimit.over_limit ? "error" : "default"}
                        label={`${roleLimit.role}: ${roleLimit.used ?? 0}/${roleLimit.limit}`}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                ) : null}
              </>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: "8px" }}>
                No active tenant license is configured. User creation and reactivation are blocked until a current license window exists.
              </Alert>
            )}
          </Stack>
        </Paper>
      ) : null}

      {/* Header + actions */}
      <Card
        variant="outlined"
        sx={{
          mb: 1.5,
          borderRadius: "8px",
          borderColor: theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16),
          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.9),
          boxShadow: "none",
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, md: 2 }, "&:last-child": { pb: { xs: 1.5, md: 2 } } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.2 }}>
                {isPlatformAdmin ? "Tenant Users and Licenses" : "Manage Users"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                {loading && users.length === 0
                  ? "Loading users…"
                  : (
                    <>
                      Showing <b>{filteredUsers.length}</b> of <b>{users.length}</b> users
                      {isPlatformAdmin && hasPlatformTenantSelection
                        ? ` for ${selectedPlatformTenant?.name || selectedPlatformTenant?.key}`
                        : ""}
                    </>
                  )}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Tooltip title="Refresh">
                <IconButton
                  onClick={fetchUsers}
                  sx={{ borderRadius: "8px", border: `1px solid ${alpha(theme.palette.divider, 0.18)}` }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={resetFilters}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}
              >
                Clear Filters
              </Button>

              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={goToImport}
                disabled={isPlatformAdmin && !hasPlatformTenantSelection}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}
              >
                Import Employees
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={goToAdd}
                disabled={isPlatformAdmin && !hasPlatformTenantSelection}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900, boxShadow: "none" }}
              >
                Add New User
              </Button>
            </Stack>
          </Stack>

          {/* Stats */}
          <Box sx={{ mt: 1.5 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6} md={3}>
                <StatCard
                  label={isPlatformAdmin ? "Provisioned Users" : "Total"}
                  value={stats.total}
                  onClick={() => setFilters((f) => ({ ...f, status: "all" }))}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard
                  label={isPlatformAdmin ? "Seats Used" : "Active"}
                  value={stats.active}
                  color={theme.palette.success.main}
                  onClick={() => setFilters((f) => ({ ...f, status: "active" }))}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="Inactive"
                  value={stats.inactive}
                  color={theme.palette.text.secondary}
                  onClick={() => setFilters((f) => ({ ...f, status: "inactive" }))}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="Suspended"
                  value={stats.suspended}
                  color={theme.palette.warning.main}
                  onClick={() => setFilters((f) => ({ ...f, status: "suspended" }))}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Filters — disabled until a tenant is selected when running as
              platform admin, otherwise the row looks live but the table
              below shows a "select a tenant" empty state. */}
          <Box
            sx={{
              mt: 1.5,
              opacity: isPlatformAdmin && !hasPlatformTenantSelection ? 0.5 : 1,
              pointerEvents:
                isPlatformAdmin && !hasPlatformTenantSelection ? 'none' : 'auto',
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Search"
                  size="small"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name, email, code, dept, role, site..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* The role/department dropdowns need room for the floating
                  label ("System Role", "Appraisal Role", "Department");
                  md=2 (~120px) was clipping them as "Syste…", "Appr…",
                  "Depa…". MUI v7's Grid takes size via the `size` prop;
                  the old `xs={12} md={3}` syntax is silently ignored. */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>System Role</InputLabel>
                  <Select
                    label="System Role"
                    value={filters.role}
                    onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {roleOptions.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Appraisal Role</InputLabel>
                  <Select
                    label="Appraisal Role"
                    value={filters.actual_role}
                    onChange={(e) => setFilters((f) => ({ ...f, actual_role: e.target.value }))}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {actualRoleOptions.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    label="Department"
                    value={filters.department}
                    onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {departmentOptions.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Advanced Filters */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                mt: 1.5,
                borderRadius: "8px",
                border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
                "&:before": { display: "none" },
                bgcolor: alpha(theme.palette.background.default, 0.32),
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <KeyboardArrowDownIcon fontSize="small" />
                  <Typography sx={{ fontWeight: 900 }}>More Filters</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    (site, assets, missing email)
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Site</InputLabel>
                      <Select
                        label="Site"
                        value={filters.site}
                        onChange={(e) => setFilters((f) => ({ ...f, site: e.target.value }))}
                      >
                        <MenuItem value="all">All</MenuItem>
                        {siteOptions.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Assets</InputLabel>
                      <Select
                        label="Assets"
                        value={filters.assets}
                        onChange={(e) => setFilters((f) => ({ ...f, assets: e.target.value }))}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="with_assets">With Assets</MenuItem>
                        <MenuItem value="no_assets">No Assets</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.4 }}>
                      *Requires assets_count from API (optional)
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={filters.missingEmail}
                          onChange={(e) =>
                            setFilters((f) => ({ ...f, missingEmail: e.target.checked }))
                          }
                        />
                      }
                      label="Only Missing Email"
                      sx={{ userSelect: "none" }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.9),
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ maxHeight: 660 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": { fontWeight: 900 },
                  "& .MuiTableCell-stickyHeader": {
                    bgcolor: theme.palette.mode === "light" ? alpha(theme.palette.primary.main, 0.035) : alpha(theme.palette.background.paper, 0.9),
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
                  },
                }}
              >
                <TableCell width={52}></TableCell>

                <TableCell sortDirection={orderBy === "employee_code" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "employee_code"}
                    direction={orderBy === "employee_code" ? order : "asc"}
                    onClick={() => handleRequestSort("employee_code")}
                  >
                    Employee Code
                  </TableSortLabel>
                </TableCell>

                <TableCell sortDirection={orderBy === "name" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleRequestSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>

                <TableCell>Email</TableCell>

                <TableCell sortDirection={orderBy === "department" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "department"}
                    direction={orderBy === "department" ? order : "asc"}
                    onClick={() => handleRequestSort("department")}
                  >
                    Department
                  </TableSortLabel>
                </TableCell>

                <TableCell>System Role</TableCell>
                <TableCell>Appraisal Role</TableCell>
                <TableCell>Status</TableCell>

                <TableCell sortDirection={orderBy === "updated_at" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "updated_at"}
                    direction={orderBy === "updated_at" ? order : "asc"}
                    onClick={() => handleRequestSort("updated_at")}
                  >
                    Updated
                  </TableSortLabel>
                </TableCell>

                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isPlatformAdmin && !hasPlatformTenantSelection ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography textAlign="center" sx={{ py: 4, color: "text.secondary", fontWeight: 700 }}>
                      Select a tenant in the super admin dashboard to load user records.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : loading ? (
                renderSkeletonRows(10)
              ) : (
                <AnimatePresence initial={false}>
                  {currentUsers.map((u) => {
                    const expanded = isExpanded(u.id);

                    return (
                      <React.Fragment key={u.id}>
                        <MotionTableRow
                          variants={rowVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          layout
                          style={{ transition: "background-color 0.15s" }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = alpha("#000", 0.02))}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                        >
                          <TableCell width={52}>
                            <Tooltip title={expanded ? "Hide details" : "Show details"}>
                              <IconButton
                                size="small"
                                onClick={() => toggleExpand(u.id)}
                                sx={{
                                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                                  transition: "transform 160ms ease",
                                  borderRadius: "8px",
                                }}
                              >
                                <ExpandMoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>

                          <TableCell sx={{ fontWeight: 900 }}>{u.employee_code || "—"}</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>{u.name || "—"}</TableCell>
                          <TableCell>
                            {u.email ? (
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {u.email}
                              </Typography>
                            ) : (
                              <Chip size="small" label="No Email" variant="outlined" color="warning" />
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={getDeptLabel(u)}>
                              {getDeptLabel(u)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip label={u.role || "—"} color={getRoleColor(u.role)} size="small" />
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={u.actual_role || "—"}
                              color={getActualRoleColor(u.actual_role)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>

                          <TableCell>
                            <Chip label={u.status || "—"} color={getStatusColor(u.status)} size="small" />
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
                              {fmtDate(u.updated_at)}
                            </Typography>
                          </TableCell>

                          <TableCell align="right">
                            <Tooltip title="View">
                              <IconButton color="primary" onClick={() => goToShow(u.id)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Edit">
                              <IconButton color="primary" onClick={() => goToEdit(u.id)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title={lower(u.status) === "active" ? "Deactivate" : "Activate"}>
                              <IconButton
                                color={lower(u.status) === "active" ? "warning" : "success"}
                                onClick={() => askToggle(u)}
                              >
                                {lower(u.status) === "active" ? <BlockIcon /> : <CheckCircleIcon />}
                              </IconButton>
                            </Tooltip>

                            <Tooltip title={isCurrentUser(u) ? "You cannot archive your own account" : "Archive"}>
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={() => askDelete(u)}
                                  disabled={isCurrentUser(u)}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </MotionTableRow>

                        {/* Expanded details row */}
                        <TableRow>
                          <TableCell colSpan={10} sx={{ p: 0, borderBottom: 0 }}>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  px: 2.5,
                                  py: 1.5,
                                  bgcolor:
                                    theme.palette.mode === "light"
                                      ? alpha(theme.palette.primary.main, 0.03)
                                      : alpha(theme.palette.primary.main, 0.08),
                                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                                }}
                              >
                                <Grid container spacing={1.5}>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      User ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {u.id}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Position
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {u.position || "—"}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Site
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {u.site || "—"}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Manager
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap title={getManagerLabel(u)}>
                                      {getManagerLabel(u)}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Cost Center
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {u.cost_center || "—"}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Contract Type
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {u.contract_type || "—"}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Nationality
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {u.nationality || "—"}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Iqama No
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {u.iqama_no || "—"}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Project ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {u.project_id ?? "—"}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Created
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {fmtDateTime(u.created_at)}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Updated
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {fmtDateTime(u.updated_at)}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} md={3}>
                                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                                      Assets Count
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                      {u.assets_count ?? u.assetsCount ?? "—"}
                                    </Typography>
                                  </Grid>
                                </Grid>

                                <Divider sx={{ my: 1.5 }} />

                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  <Chip size="small" label={`Email: ${u.email ? "Yes" : "No"}`} variant="outlined" />
                                  <Chip size="small" label={`Status: ${u.status || "—"}`} color={getStatusColor(u.status)} />
                                  <Chip size="small" label={`Role: ${u.role || "—"}`} color={getRoleColor(u.role)} />
                                  <Chip
                                    size="small"
                                    label={`Appraisal Role: ${u.actual_role || "—"}`}
                                    variant="outlined"
                                    color={getActualRoleColor(u.actual_role)}
                                  />
                                </Stack>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}

                  {!loading && currentUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Typography textAlign="center" sx={{ py: 3, color: "text.secondary", fontWeight: 700 }}>
                          No users found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </AnimatePresence>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPage}
              rowsPerPageOptions={[10, 20, 40, 80, 120]}
            />
          </Box>
        )}
      </Paper>

      {/* Toggle Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => (toggleBusy ? null : setConfirmOpen(false))}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {lower(selectedUser?.status) === "active" ? "Deactivate User" : "Activate User"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            {selectedUser ? (
              <>
                Are you sure you want to{" "}
                <b>{lower(selectedUser.status) === "active" ? "deactivate" : "activate"}</b>{" "}
                <b>{selectedUser.name}</b> ({selectedUser.employee_code})?
                <br />
                <br />
                {lower(selectedUser.status) === "active" ? (
                  <span style={{ color: "#b26a00" }}>
                    Note: Deactivation is blocked if the user has assigned assets.
                  </span>
                ) : (
                  <span style={{ color: "#1b5e20" }}>User will be able to login again.</span>
                )}
              </>
            ) : (
              "Are you sure?"
            )}
          </DialogContentText>
          {toggleAssessment.blocked ? (
            <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>
              {toggleAssessment.messages[0]}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} color="inherit" disabled={toggleBusy}>
            Cancel
          </Button>
          <Button
            onClick={confirmToggle}
            color={lower(selectedUser?.status) === "active" ? "warning" : "success"}
            variant="contained"
            autoFocus
            disabled={toggleBusy || toggleAssessment.blocked}
            startIcon={toggleBusy ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ fontWeight: 900, textTransform: "none", boxShadow: "none" }}
          >
            {lower(selectedUser?.status) === "active" ? "Deactivate" : "Activate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => (deleteBusy ? null : setDeleteOpen(false))}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Archive User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            {deleteTarget ? (
              <>
                Archive <b>{deleteTarget.name}</b> ({deleteTarget.employee_code})?
                <br />
                <br />
                <span style={{ color: "#b71c1c" }}>
                  This removes the user from active lists and revokes access tokens while keeping the record for history.
                </span>
              </>
            ) : (
              "Archive this user?"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} color="inherit" disabled={deleteBusy}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            autoFocus
            disabled={deleteBusy}
            startIcon={deleteBusy ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ fontWeight: 900, textTransform: "none", boxShadow: "none" }}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HrAdminUsers;
